import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };

type State = { error: Error | null; reactComponentStack: string | null };

/**
 * Captura erros de renderização na raiz — evita página totalmente em branco sem feedback.
 */
function describeUnknownThrown(error: unknown): string {
  try {
    if (error == null) return String(error);
    if (typeof error !== "object") return `${typeof error}: ${String(error)}`;
    const ctor = (error as { constructor?: { name?: string } }).constructor?.name ?? "Object";
    const keys = Object.getOwnPropertyNames(error).slice(0, 20);
    let json = "";
    try {
      json = JSON.stringify(error, Object.getOwnPropertyNames(error)).slice(0, 600);
    } catch {
      json = "(unserializable)";
    }
    return `${ctor} {${keys.join(",")}} ${json}`;
  } catch {
    return String(error);
  }
}

function toBoundaryError(error: unknown): Error {
  if (error instanceof Error) {
    // Mensagem vazia esconde a causa-raiz — anexa propriedades enumeráveis.
    if (!error.message) {
      const extra = describeUnknownThrown(error);
      if (extra && extra !== "Error {} {}") {
        error.message = `(empty Error message) details=${extra}`;
      }
    }
    return error;
  }
  if (typeof error === "string") return new Error(error);
  if (error != null && typeof error === "object") {
    try {
      const m =
        "message" in error && (error as { message?: unknown }).message
          ? String((error as { message?: unknown }).message)
          : describeUnknownThrown(error);
      const e = new Error(m);
      e.name =
        typeof (error as { name?: unknown }).name === "string"
          ? String((error as { name: string }).name)
          : "NonErrorThrown";
      return e;
    } catch {
      /* fall through */
    }
  }
  return new Error(String(error));
}

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reactComponentStack: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: toBoundaryError(error), reactComponentStack: null };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const err = toBoundaryError(error);
    const stack = info.componentStack ?? null;
    this.setState({ reactComponentStack: stack });
    console.error("[RootErrorBoundary]", err, info.componentStack);
    // #region agent log
    const payload = {
      sessionId: "c8e2d6",
      runId: "phase1-instrument",
      hypothesisId: "H1",
      location: "RootErrorBoundary.tsx:componentDidCatch",
      message: "Uncaught React render error",
      data: {
        errName: err.name,
        errMsg: String(err.message ?? "").slice(0, 500),
        stackPreview: String(err.stack ?? "").slice(0, 900),
        componentStackPreview: info.componentStack?.slice(0, 900) ?? "",
        url: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
      },
      timestamp: Date.now(),
    };
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("__sf_root_boundary_v1", JSON.stringify(payload.data));
      }
    } catch {
      /* private mode */
    }
    void fetch("http://127.0.0.1:7617/ingest/ff6dfea3-79f6-456a-839f-b6743986b86c", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "c8e2d6" },
      body: JSON.stringify(payload),
    }).catch(() => {});
    if (import.meta.env.DEV && typeof window !== "undefined") {
      void fetch(`${window.location.origin}/__session_debug_log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }
    // #endregion
  }

  render() {
    if (this.state.error) {
      // #region agent log
      const dbg = {
        name: this.state.error.name,
        message: this.state.error.message || "(empty message)",
        stack: this.state.error.stack || "(no stack)",
        componentStack: this.state.reactComponentStack || "(no component stack)",
      };
      // #endregion
      return (
        <div className="min-h-screen flex flex-col items-center justify-start bg-background p-6 text-foreground">
          <p className="text-lg font-semibold">Erro ao renderizar a aplicação</p>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl text-center break-words">
            <span className="font-mono">{dbg.name}</span>: {dbg.message}
          </p>
          {/* #region agent log - DEBUG c8e2d6: render full error in UI as sidechannel */}
          <details open className="mt-4 max-w-3xl w-full text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">Detalhes técnicos do erro (debug)</summary>
            <pre
              data-debug-session="c8e2d6"
              className="mt-2 max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-border bg-muted/40 p-3 text-[11px] leading-snug text-foreground"
            >
{`name: ${dbg.name}
message: ${dbg.message}

stack:
${dbg.stack}

componentStack:
${dbg.componentStack}`}
            </pre>
          </details>
          {/* #endregion */}
          <button
            type="button"
            className="mt-6 text-sm text-primary underline"
            onClick={() => this.setState({ error: null, reactComponentStack: null })}
          >
            Tentar novamente
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
